// What the signed-in screens need, worked out once per change: the store's state plus lookups and today's date.

import { useMemo } from 'react';
import { todayISO } from '../lib/dates';
import { bankHolidayMap } from '../lib/leave';
import type { Department, HolidayMap, ISODate, User } from '../types';
import type { StoreState } from './base';
import { useStoreState } from './StoreProvider';

export interface Ctx extends StoreState {
  /** Always set: these screens only render once somebody is signed in. */
  me: User;
  userById: Map<string, User>;
  deptById: Map<string, Department>;
  /** Bank holidays for the company's region. */
  bh: HolidayMap;
  today: ISODate;
  activeUsers: User[];
}

export function buildCtx(state: StoreState, today: ISODate = todayISO()): Ctx {
  if (!state.me) throw new Error('Nobody is signed in.');
  return {
    ...state,
    // Alphabetical, so every dropdown and list of job roles reads the same.
    jobRoles: state.jobRoles.slice().sort((a, b) => a.name.localeCompare(b.name, 'en-GB')),
    me: state.me,
    userById: new Map(state.users.map((u) => [u.id, u])),
    deptById: new Map(state.departments.map((d) => [d.id, d])),
    bh: bankHolidayMap(state.settings.region),
    today,
    activeUsers: state.users.filter((u) => u.active),
  };
}

export function useCtx(): Ctx {
  const state = useStoreState();
  return useMemo(() => buildCtx(state), [state]);
}
